import { formatDate, formatDateTime } from '../shared/lib/date';
import { api } from './client';
import type { PageParams } from './paging';
import type {
  AbilityKind,
  Author,
  CharacterKind,
  Dorm,
  FormAbilityEntry,
  FormField,
  FormFields,
  IsoDate,
  ModerationTarget,
  ModerationVerdict,
  MyAccountResponse,
  Page,
  ReviewStatus,
  StudyClass,
} from './types';

/**
 * Общая обвязка панелей личного кабинета: типы ответов, запросы, пара хуков
 * и выбор картинки. Всё это нужно сразу нескольким экранам кабинета, а копия
 * в каждом файле разъедется на первой же правке контракта.
 *
 * Типы описаны по тому, что реально возвращают сервисы бэкенда (Drizzle отдаёт
 * строку таблицы как есть, даты — ISO-строками), а не по тому, как поле
 * называется в БД: имена совпадают буква в букву.
 */

/* ──────────────────────────────────────────────────────────────────────────
   Типы ответов
   ────────────────────────────────────────────────────────────────────────── */

export interface CharacterRow {
  id: number;
  userId: number;
  kind: CharacterKind;
  /** Общежитие и класс назначает модератор: игрок их не вводит. */
  dorm: Dorm | null;
  studyClass: StudyClass | null;
  publishedVersionId: number | null;
  publishedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

/** Снапшот версии анкеты. Публично видна только принятая. */
export interface FormVersionRow extends FormFields {
  id: number;
  characterId: number;
  versionNo: number;
  status: ReviewStatus;
  baseVersionId: number | null;

  /** Счётчик правок заявки: правка на проверке новую версию не создаёт. */
  revision: number;
  diffFieldCodes: FormField[] | null;
  submittedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

/**
 * Статус анкеты глазами игрока. approved_draft и approved_pending значат
 * «анкета принята, а правки ещё в работе»: в реестре висит прошлая версия.
 */
export type FormStatus =
  | 'none'
  | 'draft'
  | 'pending'
  | 'approved'
  | 'approved_draft'
  | 'approved_pending';

/**
 * Анкета в кабинете: только то, по чему рисуется статус. Текста версии здесь
 * нет — за ним идут в GET /api/characters/me/form. Способности исключение: стартовые
 * берутся из принятой анкеты, а другой ручки для них нет.
 */
export interface FormSummaryRow {
  id: number;
  versionNo: number;
  status: ReviewStatus;
  abilities: FormAbilityEntry[];
  submittedAt: IsoDate | null;
  updatedAt: IsoDate;
}

/** Свой персонаж в архиве: кроме номера и даты владельцу из него не видно ничего. */
export interface ArchivedCharacterRow {
  id: number;
  deletedAt: IsoDate;
}

/**
 * Ответ GET /api/characters/me. Счётчика непрочитанных решений здесь нет:
 * он приехал на аккаунт, в MyAccountResponse (GET /api/users/me).
 */
export interface MyCharacterResponse {
  character: CharacterRow | null;
  /** Не null — персонаж в архиве; `character`, `published` и `open` тогда пусты. */
  archived: ArchivedCharacterRow | null;
  formStatus: FormStatus;
  published: FormSummaryRow | null;
  /** Черновик или заявка. Открытая версия бывает только одна. */
  open: FormSummaryRow | null;
}

export interface MyFormResponse {
  characterId: number;
  /** Та версия, которую игрок сейчас правит: открытая, иначе принятая, иначе завёрнутая. */
  version: FormVersionRow | null;
  /**
   * `open` — версия уже на сервере и правка идёт в неё; `published`/`rejected` —
   * следующее сохранение заведёт новую от неё же.
   */
  source: 'open' | 'published' | 'rejected' | null;
  publishedVersionId: number | null;
}

export interface FormFieldChange {
  field: FormField;
  kind: 'text' | 'image' | 'abilities';
  label: string;
  before: string | null;
  after: string | null;
}

export interface FormDiffResponse {
  publishedVersionId: number | null;
  openVersionId: number | null;
  fields: FormFieldChange[];
}

export interface FormDecision {
  id: number;
  /** Всегда 'form': вид решения один на все шесть, и поле в нём общее. */
  target: ModerationTarget;
  /** id версии, к которой относится решение: история и так разложена по версиям. */
  targetId: number;
  verdict: ModerationVerdict;
  /** При отказе непустой: этого требует сервис модерации. */
  comment: string | null;
  decidedAt: IsoDate;
  moderator: Author;
}

/**
 * Строка своей ленты решений — ответ GET /api/decisions/me. Сверх решения
 * только название вещи: персонаж тут всегда свой, а открыть саму версию
 * хватает пары target + targetId.
 */
export interface MyDecision extends FormDecision {
  /** Название предмета, питомца или способности; у анкеты — ФИО из решённой версии. */
  subject: string | null;
}

/** Строка истории анкеты: текст версии за ней, по versionId. */
export interface FormHistoryItem {
  versionId: number;
  versionNo: number;
  status: ReviewStatus;
  submittedAt: IsoDate | null;
  /** У снятой версии остаётся: по паре соседних видно, сколько она продержалась. */
  publishedAt: IsoDate | null;
  isPublished: boolean;
  decisions: FormDecision[];
}

/* ── Имущество персонажа ──────────────────────────────────────────────────
   Предмет, питомец и способность устроены одинаково: строка сущности плюс
   версии. Черновиков здесь нет: заявка уходит на проверку сразу.
   Строка списка плоская — какую версию показать, решает сервер, а что
   показывать, говорит `state` у запроса.
   ────────────────────────────────────────────────────────────────────── */

export interface BaseRecord {
  id: number;
  /** Не null = в архиве. Причину указывать необязательно. */
  deletedAt: IsoDate | null;
  deleteReason: string | null;
  versionNo: number;
  /** Статус ПОКАЗАННОЙ версии. */
  status: ReviewStatus;
  submittedAt: IsoDate | null;
  /** Не null — поверх принятой версии едет правка. */
  pendingSince: IsoDate | null;
}

/** Общее у версии предмета, питомца и способности. */
export interface BaseVersion {
  id: number;
  /** 1..N в пределах сущности. Как versionNo у версии анкеты. */
  versionNo: number;
  status: ReviewStatus;
  /** Счётчик правок заявки: правка на проверке новую версию не создаёт. */
  revision: number;
  createdById: number;
  submittedAt: IsoDate | null;
  /**
   * Когда версия попала в анкету. У неопубликованных null, у снятых остаётся:
   * по паре дат соседних версий видно, сколько каждая продержалась.
   */
  publishedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface ItemVersionRow extends BaseVersion {
  itemId: number;
  title: string;
  description: string | null;
  imageId: string | null;
}

export interface ItemView extends BaseRecord {
  /** Личные метки владельца: модерации не проходят, живут на предмете. */
  tags: string[];
  title: string;
  description: string | null;
  imageId: string | null;
}

export interface PetVersionRow extends BaseVersion {
  petId: number;
  name: string;
  species: string | null;
  description: string | null;
  imageId: string | null;
  passportImageId: string | null;
}

export interface PetView extends BaseRecord {
  name: string;
  species: string | null;
  description: string | null;
  imageId: string | null;
  passportImageId: string | null;
}

export interface AbilityVersionRow extends BaseVersion {
  abilityId: number;
  /** Что заявлено: умение или дебаф. Про происхождение говорит sourceLink. */
  kind: AbilityKind;
  title: string;
  description: string | null;
  /** Ссылка на квест, пост или отыгрыш, где это случилось. Необязательна. */
  sourceLink: string | null;
  /** Картинка выдачи, как у предмета склада: id строки images или null. */
  imageId: string | null;
  expiresAt: IsoDate | null;
}

export interface AbilityView extends BaseRecord {
  kind: AbilityKind;
  title: string;
  description: string | null;
  sourceLink: string | null;
  imageId: string | null;
  expiresAt: IsoDate | null;
  /**
   * Срок вышел сам. Считается на чтении, отдельного статуса в базе нет.
   * Это НЕ то же, что deletedAt: истёкшую никто не отзывал.
   */
  expired: boolean;
}

/** Тело автосохранения анкеты. Присланное записывается, пропущенное не трогается. */
/** Тело PATCH: шлём только изменившееся, неприсланное сервер берёт из базы. */
export type SaveFormBody = Partial<FormFields>;

/**
 * Тела заявок на имущество. Пропущенное поле сервер берёт из предыдущей
 * версии, поэтому картинку шлют, только когда её меняли: чужую он не примет,
 * а после передачи персонажа фото остаются за прежним владельцем. Снятая
 * картинка — это null, а не пропуск поля.
 */
export interface ItemBody {
  title?: string;
  description?: string | null;
  imageId?: string | null;
}

export interface PetBody {
  name?: string;
  species?: string | null;
  description?: string | null;
  imageId?: string | null;
  passportImageId?: string | null;
}

export interface AbilityBody {
  /**
   * Вид заявки: способность или дебаф. Стартовые способности живут в анкете
   * и через этот маршрут не идут — вида у них нет вовсе.
   */
  kind?: AbilityKind;
  title?: string;
  description?: string | null;
  /** Откуда: ссылка на квест, пост или отыгрыш. Пусто — тоже ответ. */
  sourceLink?: string | null;
  imageId?: string | null;
  expiresAt?: string | null;
}

/* ──────────────────────────────────────────────────────────────────────────
   Запросы
   ────────────────────────────────────────────────────────────────────────── */

export type PropertyState = 'published' | 'requests' | 'archived';

/** Тип, а не интерфейс: у интерфейса нет неявной индексной сигнатуры, и он не подходит под QueryParams. */
export type PropertyListFilter = {
  search?: string;
  state?: PropertyState;
};

export type ItemListFilter = PropertyListFilter & {
  tag?: string;
};

export const cabinetApi = {
  character: () => api.get<MyCharacterResponse>('/api/characters/me'),
  createCharacter: () => api.post<CharacterRow>('/api/characters'),

  /**
   * Настройки аккаунта и счётчик непрочитанных решений. Кабинету нужен только
   * счётчик, но маршрут отдаёт обе величины разом: они обе про аккаунт.
   */
  settings: () => api.get<MyAccountResponse>('/api/users/me'),
  /**
   * Отметка о прочтении лежит в read_marker, а та ключуется по аккаунту:
   * персонажа в ней нет вовсе, поэтому маршрут аккаунтский, а не кабинетный.
   */
  markDecisionsRead: () => api.post<{ readAt: IsoDate }>('/api/users/me/decisions/read'),

  form: () => api.get<MyFormResponse>('/api/characters/me/form'),
  saveForm: (body: SaveFormBody) => api.patch<FormVersionRow>('/api/characters/me/form', body),
  submitForm: () => api.post<FormVersionRow>('/api/characters/me/form/submit'),
  formDiff: () => api.get<FormDiffResponse>('/api/characters/me/form/diff'),
  formHistory: (params: PageParams, signal?: AbortSignal) =>
    api.get<Page<FormHistoryItem>>('/api/characters/me/form/history', { ...params }, signal),
  myDecisions: (params: PageParams, signal?: AbortSignal) =>
    api.get<Page<MyDecision>>('/api/decisions/me', { ...params }, signal),
  formVersion: (versionId: number) =>
    api.get<FormVersionRow>(`/api/characters/me/form/versions/${versionId}`),
  itemVersion: (versionId: number) =>
    api.get<ItemVersionRow>(`/api/inventory/versions/${versionId}`),
  petVersion: (versionId: number) => api.get<PetVersionRow>(`/api/pets/versions/${versionId}`),
  abilityVersion: (versionId: number) =>
    api.get<AbilityVersionRow>(`/api/abilities/versions/${versionId}`),

  items: (filter: ItemListFilter, params: PageParams, signal?: AbortSignal) =>
    api.get<Page<ItemView>>('/api/inventory', { ...filter, ...params }, signal),
  /**
   * Метки считает сервер по обеим группам сразу: у архива свои, а фильтр на
   * экране один на оба режима. Собрать их по загруженному списку нельзя —
   * список постраничный.
   */
  itemTags: () => api.get<{ tags: string[] }>('/api/inventory/tags'),
  createItem: (body: ItemBody) => api.post<ItemView>('/api/inventory', body),
  updateItem: (itemId: number, body: ItemBody) =>
    api.patch<ItemView>(`/api/inventory/${itemId}`, body),
  // Метки правятся отдельной ручкой: версию они не создают и на проверку не идут.
  updateItemTags: (itemId: number, tags: string[]) =>
    api.patch<ItemView>(`/api/inventory/${itemId}/tags`, { tags }),
  archiveItem: (itemId: number, reason?: string) =>
    api.delete<{ ok: true }>(`/api/inventory/${itemId}`, archiveBody(reason)),

  pets: (filter: PropertyListFilter, params: PageParams, signal?: AbortSignal) =>
    api.get<Page<PetView>>('/api/pets', { ...filter, ...params }, signal),
  createPet: (body: PetBody) => api.post<PetView>('/api/pets', body),
  updatePet: (petId: number, body: PetBody) => api.patch<PetView>(`/api/pets/${petId}`, body),
  archivePet: (petId: number, reason?: string) =>
    api.delete<{ ok: true }>(`/api/pets/${petId}`, archiveBody(reason)),

  abilities: (filter: PropertyListFilter, params: PageParams, signal?: AbortSignal) =>
    api.get<Page<AbilityView>>('/api/abilities', { ...filter, ...params }, signal),
  createAbility: (body: AbilityBody) => api.post<AbilityView>('/api/abilities', body),
  updateAbility: (id: number, body: AbilityBody) =>
    api.patch<AbilityView>(`/api/abilities/${id}`, body),
  archiveAbility: (id: number, reason?: string) =>
    api.delete<{ ok: true }>(`/api/abilities/${id}`, archiveBody(reason)),
};

/** Пустую причину не шлём вовсе: поле необязательное, а пустая строка не пройдёт валидацию. */
function archiveBody(reason?: string): { reason?: string } {
  const trimmed = reason?.trim();
  return trimmed ? { reason: trimmed } : {};
}

/* Даты форматирует shared/lib/date — здесь только проброс для экранов
   кабинета, которые и так импортируют отсюда всё остальное. */
export { formatDate, formatDateTime };
